import { Lock } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { useEffect } from "react";
import { trackEvent } from "../../services/analytics";

/**
 * PlanLock — single canonical subscription-lock component for the
 * dashboard. Replaces the prior zoo of plan-gate UIs (PlanGate full-card,
 * IntegrationHub local SectionEmpty "Upgrade to Scale", FederatedLearning
 * UPGRADE_REQUIRED EmptyState, inline muted-text fallbacks, etc.) under
 * one consistent visual + copy + CTA surface.
 *
 *   variant="page"    — full lock card with feature-list + 2 CTAs.
 *                       Use for RequirePlan-route-wrappers and
 *                       UpgradeModal bodies (entire page or modal is
 *                       blocked until upgrade).
 *   variant="section" — section-scoped lock with 1 CTA. Use for
 *                       in-page section gating (Webhooks, Settings SSO
 *                       activity panel, TeamManagement audit log).
 *
 * Inline lock indicators (Sidebar nav items, disabled-button tooltips)
 * are NOT this component — they're just a Lucide <Lock /> icon plus
 * a `planTooltip()` string. Keep them lightweight; PlanLock is for
 * surfaces with their own block of vertical space.
 */

export type RequiredPlan = "sandbox" | "scale" | "governance" | "enterprise" | "custom";

/** Plan name source of truth. Used everywhere a plan is referenced — no
 *  hand-rolled "Professional" / "Custom-tier" strings sprinkled in copy.
 *  Backend FEATURE_GATES keys MUST match this list exactly. */
export const PLAN_NAMES: Record<RequiredPlan, string> = {
  sandbox: "Sandbox",
  scale: "Scale",
  governance: "Governance",
  enterprise: "Enterprise",
  custom: "Custom",
};

/** Plan tier order — used by planMeets() to compare user plan vs required. */
export const PLAN_ORDER = ["sandbox", "scale", "governance", "enterprise", "custom"] as const;

/** Single CTA destination for "upgrade your subscription". In-app billing
 *  flow rather than the public /pricing page because the user is logged in
 *  — same convention as Stripe / Linear / Notion. */
export const UPGRADE_PATH = "/dashboard/billing";

/** Secondary CTA — used when the user wants to talk to a human (Custom-tier
 *  features that require contract negotiation). */
export const SALES_HREF = "mailto:sales@adjudon.com";

/** True iff `userPlan` is at least at `required` tier. Returns false for
 *  unknown plan strings so a backend drift never silently elevates. */
export function planMeets(userPlan: string, required: RequiredPlan): boolean {
  const userIdx = PLAN_ORDER.indexOf(userPlan as (typeof PLAN_ORDER)[number]);
  const reqIdx = PLAN_ORDER.indexOf(required);
  if (userIdx < 0 || reqIdx < 0) return false;
  return userIdx >= reqIdx;
}

/**
 * Canonical tooltip copy for inline lock indicators. Use this everywhere
 * a Lock-icon shows up next to a disabled button or sidebar item — keeps
 * "Governance+", "or above", "tier", "plan" wording aligned across the
 * dashboard.
 *
 *   planTooltip("scale")               → "Requires Scale plan or above"
 *   planTooltip("scale", "Webhooks")   → "Webhooks · Scale or above"
 */
export function planTooltip(required: RequiredPlan, featureName?: string): string {
  if (featureName) return `${featureName} · ${PLAN_NAMES[required]} or above`;
  return `Requires ${PLAN_NAMES[required]} plan or above`;
}

const PLAN_ACCENT: Record<RequiredPlan, string> = {
  sandbox: "border-l-border",
  scale: "border-l-[var(--brand)]",
  governance: "border-l-review",
  enterprise: "border-l-confidence",
  custom: "border-l-foreground",
};

interface PlanLockProps {
  variant: "page" | "section";
  requiredPlan: RequiredPlan;
  featureName: string;
  /** One-liner shown on `section`; longer prose on `page`. */
  description?: string;
  /** Feature bullets shown on `page`. Recommended 4-6. Ignored on `section`. */
  features?: string[];
  className?: string;
  testId?: string;
}

/** Tracks the lock impression once on mount. Same telemetry signal as the
 *  prior PlanGate so the funnel dashboards don't lose history. */
function useLockTracking(requiredPlan: RequiredPlan, featureName: string) {
  useEffect(() => {
    trackEvent("plan_gate_hit", { plan: requiredPlan, component: featureName });
  }, [requiredPlan, featureName]);
}

export function PlanLock({
  variant,
  requiredPlan,
  featureName,
  description,
  features,
  className = "",
  testId,
}: PlanLockProps) {
  const navigate = useNavigate();
  const planLabel = PLAN_NAMES[requiredPlan];
  useLockTracking(requiredPlan, featureName);

  const handleUpgrade = () => {
    trackEvent("upgrade_clicked", { plan: requiredPlan, source: variant });
    navigate(UPGRADE_PATH);
  };

  if (variant === "page") {
    return (
      <div
        className={`adp-card border-l-2 ${PLAN_ACCENT[requiredPlan]} p-8 ${className}`}
        role="status"
        aria-live="polite"
        data-testid={testId}
      >
        {/* Header: Lock-icon-box + h2 */}
        <div className="flex items-start gap-4 mb-6">
          <div className="p-2.5 border border-border bg-surface-2 flex-shrink-0">
            <Lock className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          </div>
          <div>
            <div className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider mb-1">
              Access Restricted
            </div>
            <h2 className="font-semibold text-foreground">
              {featureName} requires {planLabel} or above
            </h2>
          </div>
        </div>

        {description && (
          <p
            className="text-sm text-muted-foreground mb-6 max-w-2xl"
            style={{ lineHeight: "1.55" }}
          >
            {description}
          </p>
        )}

        <div className="border-t border-border mb-6" />

        {/* Required plan badge */}
        <div className="mb-6">
          <div className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider mb-2">
            Required Plan
          </div>
          <div className="text-sm font-bold text-mono uppercase tracking-wider text-[var(--brand)]">
            {planLabel}
          </div>
        </div>

        {features && features.length > 0 && (
          <>
            <div className="border-t border-border mb-6" />
            <div className="mb-8">
              <div className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider mb-3">
                Included in {planLabel}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {features.map((f) => (
                  <div key={f} className="flex items-start gap-2.5">
                    <div className="w-1 h-1 bg-muted-foreground mt-[7px] flex-shrink-0" />
                    <span className="text-sm text-foreground">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={handleUpgrade}
            data-testid={testId ? `${testId}-upgrade` : undefined}
            className="adp-btn adp-btn-primary px-5 py-2 text-xs text-mono uppercase tracking-wider flex items-center gap-1.5"
          >
            Upgrade to {planLabel} →
          </button>
          <a
            href={SALES_HREF}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("contact_sales_clicked", { plan: requiredPlan, source: variant })}
            data-testid={testId ? `${testId}-sales` : undefined}
            className="adp-btn px-5 py-2 text-xs text-mono uppercase tracking-wider bg-transparent hover:bg-surface-1 border border-border text-muted-foreground hover:text-foreground"
          >
            Contact Sales
          </a>
        </div>
      </div>
    );
  }

  // section variant — horizontal-first layout: icon left, content middle,
  // CTA right. Uses card's full horizontal space instead of leaving a
  // big chunk of whitespace on either side of a vertical stack. On
  // mobile (< sm) it collapses to the vertical stack pattern so narrow
  // screens still read cleanly. Same density / typography as
  // EmptyState section variant — both live in the same `space-y-6`
  // Stacks and need to feel like cousins.
  return (
    <div
      role="status"
      aria-live="polite"
      className={`adp-card flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-center sm:text-left ${className}`}
      data-testid={testId}
    >
      <div className="p-3 border border-border bg-surface-2 flex-shrink-0">
        <Lock className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
      </div>

      <div className="flex-1 min-w-0">
        <h3
          className="text-base sm:text-lg font-semibold text-foreground"
          style={{ lineHeight: "1.3", letterSpacing: "-0.005em" }}
        >
          {featureName} requires {planLabel} or above
        </h3>
        {description && (
          <p
            className="mt-1 text-xs sm:text-sm text-muted-foreground"
            style={{ lineHeight: "1.55" }}
          >
            {description}
          </p>
        )}
      </div>

      <div className="flex-shrink-0">
        <Link
          to={UPGRADE_PATH}
          onClick={() => trackEvent("upgrade_clicked", { plan: requiredPlan, source: variant })}
          data-testid={testId ? `${testId}-upgrade` : undefined}
          className="adp-btn adp-btn-primary inline-flex items-center gap-2 px-3 py-1.5 min-h-[32px] text-xs font-semibold"
        >
          Upgrade to {planLabel}
        </Link>
      </div>
    </div>
  );
}
